import { zipObject } from 'lodash';
import { APPSERVER_API_PREFIX } from '@/main/app.constants';
import { getAxiosInstance } from '@/requests/axios.utilities';
import { sqMaterializedTablesApi } from '@/sdk/api/MaterializedTablesApi';
import { sqTablesApi } from '@/sdk/api/TablesApi';
import { downloadBlob } from '@/utilities/download.utilities';
import type { SeeqAxiosRequestConfig } from '@/requests/axios.utilities';
import type { PropertiesForItemUUIDColumn, Filter as SdkFilter, TableResultOutputV1 } from '@/sdk/model/models';
import type { Filter as MaterializedTableFilter } from '@/serverSideTable/materializedTables/materializedTables.constants';
import type { GroupBy } from '@/serverSideTable/serverSideTable.constants';
import type { AnyProperty } from '@/utilities.types';

interface Event {
  properties: { name: string; value: unknown; unitOfMeasure?: string }[];
}

export async function publish(tableId: string, events: Event[]) {
  const response = await sqMaterializedTablesApi.publishEvents(
    {
      events: events.map((event) => ({ genericEvent: event })),
    },
    { tableId },
  );
  return response.data;
}

export function tabulate({ rows, headers }: TableResultOutputV1): AnyProperty[] {
  const headerNames = headers.map((h) => h.name);

  return rows.map((row) => zipObject(headerNames, row));
}

export async function getTable(
  tableId: string,
  {
    limit,
    columns,
    properties,
    filter,
    sorts,
    cursor,
    useDistinct,
    virtualColumns,
    groupBy,
    conditionMonitorId,
  }: {
    limit: number;
    columns: string[];
    properties?: PropertiesForItemUUIDColumn[];
    filter?: MaterializedTableFilter;
    sorts?: { columnName: string; ascending: boolean }[];
    cursor?: { cursorKey: string };
    useDistinct?: boolean;
    virtualColumns?: { vantage: string[] };
    groupBy?: GroupBy;
    conditionMonitorId?: string;
  },
  cancellationGroup?: string,
) {
  const config: SeeqAxiosRequestConfig = {};
  if (cancellationGroup) {
    config.cancellationGroup = cancellationGroup;
    config.cancelOnServer = true;
  }
  const { data } = await sqTablesApi.queryTableData(
    {
      limit,
      columnsToInclude: columns,
      propertiesToInclude: properties,
      filter: filter as unknown as SdkFilter,
      sorts,
      cursor,
      useDistinct,
      virtualColumns,
      groupBy,
      conditionMonitorId,
    },
    { tableId },
    config,
  );

  return { data: tabulate(data), cursorKey: data.cursorKey };
}

/**
 * Fetch distinct values for a specific column in a materialized table
 *
 * @param tableDefinitionId - The ID of the table definition for the materialized table
 * @param columnName - The name of the column to fetch distinct values for
 */
export async function fetchDistinctValuesForColumn<ColumnType = string>(
  tableDefinitionId: string,
  columnName: string,
  limit: number,
): Promise<ColumnType[]> {
  const response = await getTable(tableDefinitionId, {
    limit,
    useDistinct: true,
    columns: [columnName],
  });

  return response.data.map((item) => item[columnName]);
}

export const MAX_CSV_EXPORT_ROWS = 10_000;

/**
 * Exports table data as a CSV file, respecting the provided filters and sorts, up to
 * MAX_CSV_EXPORT_ROWS rows.
 *
 * @returns `true` if the export was truncated at the row limit, `false` otherwise.
 */
export async function exportTableToCsv(
  tableId: string,
  params: {
    filter?: MaterializedTableFilter;
    sorts?: { columnName: string; ascending: boolean }[];
    columnsToInclude?: string[];
    virtualColumns?: { vantage: string[] };
    conditionMonitorId?: string;
    columnHeaders?: Record<string, string>;
    columnOrder?: string[];
  },
  fallbackFilename?: string,
): Promise<boolean> {
  const response = await getAxiosInstance().post(
    `${APPSERVER_API_PREFIX}/tables/${tableId}/data/csv`,
    {
      limit: MAX_CSV_EXPORT_ROWS,
      filter: params.filter as unknown as SdkFilter,
      sorts: params.sorts,
      columnsToInclude: params.columnsToInclude,
      virtualColumns: params.virtualColumns,
      conditionMonitorId: params.conditionMonitorId,
      columnHeaders: params.columnHeaders,
      columnOrder: params.columnOrder,
    },
    { responseType: 'blob', headers: { Accept: 'text/csv' } },
  );

  downloadBlob({ blob: response.data as Blob, fallbackFilename });
  return response.headers['x-sq-export-truncated'] === 'true';
}
